import React from "react";
import { ThemeColor } from "../../props";
import { cn } from "../../styles";
import { Panel, PanelProps } from "../Panel";

export interface ToolbarPanelProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

export type ToolbarPanelContentProps = PanelProps;

export interface ToolbarPanelToolbarProps extends PanelProps {
  /**
   * Reduces toolbar padding for compact contexts.
   * @default false
   */
  dense?: boolean;
}

/**
 * Content area that fills remaining height.
 */
const ToolbarPanelContent = ({
  children,
  className,
  color = ThemeColor.none,
  rounded = false,
  shadow = false,
  border = false,
  ...props
}: ToolbarPanelContentProps) => (
  <Panel
    color={color}
    rounded={rounded}
    shadow={shadow}
    border={border}
    className={cn("flex-1 min-h-0 p-0", className)}
    {...props}
  >
    {children}
  </Panel>
);

ToolbarPanelContent.displayName = "ToolbarPanel.Content";

/**
 * Pinned bottom toolbar separated from content by a border.
 * Children are laid out as a flex row. Use `ml-auto` on a group to push it to the right.
 */
const ToolbarPanelToolbar = ({
  children,
  className,
  color = ThemeColor.none,
  rounded = false,
  shadow = false,
  border = false,
  dense = false,
  ...props
}: ToolbarPanelToolbarProps) => (
  <Panel
    color={color}
    rounded={rounded}
    shadow={shadow}
    border={border}
    className={cn(
      "flex-shrink-0 border-t border-back-200 dark:border-back-700",
      "flex items-center gap-3",
      dense ? "px-3 py-2" : "px-4 py-3",
      className,
    )}
    {...props}
  >
    {children}
  </Panel>
);

ToolbarPanelToolbar.displayName = "ToolbarPanel.Toolbar";

/**
 * Full-height column layout with a content area and a pinned bottom toolbar.
 * Designed for wizards, confirmations, and detail views where actions
 * must remain visible regardless of content length.
 *
 * @example
 * <ToolbarPanel>
 *   <ToolbarPanel.Content>
 *     {body}
 *   </ToolbarPanel.Content>
 *   <ToolbarPanel.Toolbar>
 *     <Button variant={ButtonVariant.outlined}>Cancel</Button>
 *     <Button color={ThemeColor.primary}>Continue</Button>
 *   </ToolbarPanel.Toolbar>
 * </ToolbarPanel>
 */
export const ToolbarPanel = ({
  children,
  className,
  ...props
}: ToolbarPanelProps) => (
  <div className={cn("flex h-full flex-col", className)} {...props}>
    {children}
  </div>
);

ToolbarPanel.displayName = "ToolbarPanel";
ToolbarPanel.Content = ToolbarPanelContent;
ToolbarPanel.Toolbar = ToolbarPanelToolbar;

export { ToolbarPanelContent, ToolbarPanelToolbar };
